import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { APPROVAL_STATUSES, type Paginated } from '@insurensa/shared';
import { Badge, Card, DataTable, PageTitle, type DataTableColumn } from '@insurensa/ui';
import { useFormat, useT } from '@insurensa/i18n';
import { api, qs } from '../lib/api.js';
import { ErrorAlert } from '../components/ui.js';

interface PartnerRow {
  id: string;
  companyName: string;
  email: string;
  registrationNumber: string;
  approvalStatus: string;
  seenByAdmin: boolean;
  postalCode: string | null;
  address: string | null;
  offerCount: number;
  leadCount: number;
  createdAt: string;
}

/**
 * Replaces Masterdata's insurance_comp.php / partners.php listing, including
 * the DataTables toolbar those pages carried — sorting, search, page length and
 * the Copy/CSV/Excel/PDF/Print export row.
 */
export function PartnersPage(): JSX.Element {
  const t = useT();
  const fmt = useFormat();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const status = searchParams.get('status') ?? '';
  const search = searchParams.get('search') ?? '';

  const { data, error, isLoading } = useQuery({
    queryKey: ['partners', status, search, page, pageSize],
    queryFn: () =>
      api.get<Paginated<PartnerRow>>(`/admin/partners${qs({ status, search, page, pageSize })}`),
  });

  const setParam = (key: string, value: string): void => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    setSearchParams(params);
    setPage(1);
  };

  const columns: Array<DataTableColumn<PartnerRow>> = [
    {
      key: 'companyName',
      header: t('fields.partner'),
      value: (row) => row.companyName,
      render: (row) => (
        <>
          <Link to={`/partners/${row.id}`}>
            <strong>{row.companyName}</strong>
          </Link>
          <div className="small text-muted">{row.email}</div>
        </>
      ),
    },
    {
      key: 'registrationNumber',
      header: t('fields.registrationNumberShort'),
      value: (row) => row.registrationNumber,
    },
    { key: 'postalCode', header: t('fields.postalCodeShort'), value: (row) => row.postalCode ?? fmt.empty },
    {
      key: 'approvalStatus',
      header: t('common.status'),
      value: (row) => fmt.approval(row.approvalStatus),
      render: (row) => (
        <Badge value={row.approvalStatus}>{fmt.approval(row.approvalStatus)}</Badge>
      ),
    },
    { key: 'leadCount', header: t('fields.leads'), numeric: true, value: (row) => String(row.leadCount) },
    { key: 'offerCount', header: t('fields.offers'), numeric: true, value: (row) => String(row.offerCount) },
    {
      key: 'createdAt',
      header: t('fields.registeredAt'),
      value: (row) => fmt.date(row.createdAt),
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      exportable: false,
      render: (row) => (
        <Link to={`/partners/${row.id}`} className="btn btn-primary btn-sm">
          {t('common.open')}
        </Link>
      ),
    },
  ];

  return (
    <>
      <PageTitle
        heading={t('admin.nav.partners')}
        subheading={t('admin.partners.subheading')}
        icon="pe-7s-culture"
      />

      <ErrorAlert error={error} />

      <Card noPadding>
        <div className="card-body">
          <DataTable
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(row) => row.id}
            loading={isLoading}
            exportFileName="partner"
            exportTitle={t('admin.partners.exportTitle')}
            emptyMessage={t('admin.partners.empty')}
            search={{ value: search, onChange: (value) => setParam('search', value) }}
            pagination={{
              page: data?.page ?? page,
              pageSize: data?.pageSize ?? pageSize,
              total: data?.total ?? 0,
              totalPages: data?.totalPages ?? 1,
              onPage: setPage,
              onPageSize: (next) => {
                setPageSize(next);
                setPage(1);
              },
            }}
            onExportAll={async () => {
              // DataTables held the whole table client-side, so its export
              // covered every row. Match that by fetching the full set.
              const all = await api.get<Paginated<PartnerRow>>(
                `/admin/partners${qs({ status, search, page: 1, pageSize: 100 })}`,
              );
              return all.items;
            }}
            toolbar={
              <div className="form-inline mt-2">
                <label className="mr-2 mb-0 small text-muted" htmlFor="status">
                  {t('common.status')}
                </label>
                <select
                  id="status"
                  className="form-control form-control-sm"
                  value={status}
                  onChange={(event) => setParam('status', event.target.value)}
                >
                  <option value="">{t('common.all')}</option>
                  {APPROVAL_STATUSES.map((value) => (
                    <option key={value} value={value}>
                      {fmt.approval(value)}
                    </option>
                  ))}
                </select>
              </div>
            }
          />
        </div>
      </Card>
    </>
  );
}
